/**
 * The Tasks calendar's Day and Week views (robot lanes), and the toolbar,
 * selection and panel they share with the Agenda (`AgendaView`, a table
 * by day with the repeating schedules beside it; 2026-10-30).
 *
 * Replaces the hour-grid Day/Week, where a task was as tall as it was
 * long -- a five-minute delivery was a sliver, overlaps were squeezed
 * into thin columns, and nothing said which robot did what. Here each
 * robot is a row and time runs across (Day), or each robot's day is one
 * cell (Week). Month is `MonthView` (rebuilt 2026-10-31).
 *
 * Draws `LaneData` (`useCalendarLaneData`) and fetches nothing itself.
 * Everything a viewer may DO arrives as a callback the screen withholds
 * when their role may not; see `LanePanel`.
 *
 * AT 1280 (2026-10-30). Rows are compact by default (one line per robot,
 * a Comfy/Compact switch remembered per browser), and the selected task's
 * panel floats over the right of the timeline instead of taking a
 * column, so the lanes get the full width. × or Esc closes it and focus
 * goes back to the chip it came from.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link, useInRouterContext } from 'react-router-dom';
import type { TaskState } from '../../../api/rmf-client';
import type { MaintenanceWindow, ScheduleOccurrence, TaskSchedule } from '../../../api/v1';
import type { LanePaint } from '../calendar-categories';
import { browserTimeZone, formatZonedClock, timeZoneLabel } from '../zoned-time';
import { AGENDA_MAX_DAYS, AGENDA_STEP_DAYS, type CalendarViewKey } from './calendar-url';
import { agendaDays, scheduleSelectionId } from './agenda-model';
import { AgendaView } from './AgendaView';
import { buildScheduleRows, type CalendarIssue } from './phone-model';
import { linenRobotKeys } from './robot-groups';
import { SchedulePanel } from './SchedulePanel';
import type { ScheduleActions } from './useScheduleActions';
import { BlockTimeDialog, type BlockTimePreset } from './BlockTimeDialog';
import type { LiftCalendar } from './useLiftCalendar';
import { loadLaneDensity, saveLaneDensity, type LaneDensity } from './calendar-prefs';
import { DayLanes } from './DayLanes';
import { DIDNT_FINISH, paintOf, type LaneItem } from './lane-items';
import { COMFY_METRICS, COMPACT_METRICS, buildBoard } from './lane-layout';
import { LANE_CODE, LANE_PAINTS, laneVars, paintLabel } from './lane-style';
import {
  WEEKDAY_LONG,
  addDays,
  dateKeyOf,
  formatKey,
  weekLabel,
  weekdayOf,
  type DateKey,
} from './lane-time';
import { LanePanel, type EditScheduleRequest, type LanePanelProps } from './LanePanel';
import { samePlan } from './schedule-span';
import type { LaneData } from './useCalendarLaneData';
import { WeekLanes } from './WeekLanes';
import './CalendarLanes.css';

export interface LanesNavigation {
  cal?: CalendarViewKey;
  /** Null = today. */
  date?: DateKey | null;
  robot?: string;
  fullDay?: boolean;
  /** The Agenda's range, in days. */
  days?: number;
}

/** What only the Agenda needs (`CalendarLanesContainer` gathers it). */
export interface AgendaExtras {
  /** Days shown from `dateKey`. */
  days: number;
  /** Occurrences each schedule's next runs are read from. */
  occurrences: readonly ScheduleOccurrence[];
  horizonDays: number;
  issues: readonly CalendarIssue[];
  actions: ScheduleActions;
  mySub: string | null;
}

export interface CalendarLanesProps {
  siteId: string;
  view: 'day' | 'week' | 'agenda';
  dateKey: DateKey;
  /** The URL names no day: `dateKey` is today, and moves at midnight
   *  without the operator paging. */
  followsToday?: boolean;
  todayKey: DateKey;
  timeZone: string;
  nowMs: number;
  data: LaneData;
  schedules: readonly TaskSchedule[];
  robotFilter: string;
  fullDay: boolean;
  onNavigate: (patch: LanesNavigation) => void;
  onOpenTask?: (item: LaneItem) => void;
  onSkipOccurrence?: (item: LaneItem) => void;
  onMoveOccurrence?: (item: LaneItem, newStartMs: number) => void;
  onPauseSeries?: LanePanelProps['onPauseSeries'];
  /** Open the schedule editor (the Agenda's schedule panel, a run's
   *  panel). Withheld when this viewer may not edit schedules. */
  onEditSchedule?: (req: EditScheduleRequest) => void;
  onCreateAtSlot?: (slot: { start: Date; end: Date }) => void;
  onCreateMaintenance?: (range: { startMs: number; endMs: number; reason?: string }) => void;
  /** Lift rules on the calendar. Its `create`, `remove` and `skip` come only when
   *  this viewer may change them. */
  liftCalendar?: LanePanelProps['liftCalendar'] & Partial<Pick<LiftCalendar, 'create' | 'choices' | 'state'>>;
  /** Open Block time on this choice: the lift panel's "Add to calendar…". */
  blockPreset?: BlockTimePreset | null;
  /** Remove a blocked time. When it answers whether the removal took,
   *  a confirmed Remove from the lane panel closes the panel on yes. */
  onDeleteMaintenance?: (w: MaintenanceWindow) => void | Promise<boolean>;
  onNewTask?: () => void;
  renderTaskActions?: (task: TaskState) => ReactNode;
  /** Where "Edit groups" goes; shown only when the site lets this viewer
   *  edit its robot groups. */
  editGroupsHref?: string;
  /** The signed-in user's subject: "you" for what they made. */
  mySub?: string | null;
  /** A view-as-role preview: look only, so nothing can be dragged. */
  lookOnly?: boolean;
  /** Required for the Agenda; ignored by Day and Week. */
  agenda?: AgendaExtras;
  /** A schedule the editor has just saved (a new `nonce` per save). A
   *  timing edit gives the run selected in the panel a new id -- its new
   *  instant, or the carry-over row it moved to -- so the panel finds
   *  that schedule's run on the same day again rather than saying the
   *  task is gone and dropping focus with its buttons. */
  savedSchedule?: { scheduleId: string; nonce: number } | null;
}

const COLLAPSE_KEY = 'nx:cal-lanes-collapsed';

function loadCollapsed(siteId: string): Set<string> {
  try {
    const raw = window.localStorage.getItem(`${COLLAPSE_KEY}:${siteId}`);
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(
      Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string') : [],
    );
  } catch {
    return new Set();
  }
}

function saveCollapsed(siteId: string, s: ReadonlySet<string>): void {
  try {
    window.localStorage.setItem(`${COLLAPSE_KEY}:${siteId}`, JSON.stringify([...s]));
  } catch {
    // A lost display preference is not worth failing over.
  }
}

/** Block time's lift choices, when this viewer may set one. */
function blockTimeLifts(cal: CalendarLanesProps['liftCalendar']): LiftCalendar | undefined {
  return cal?.create ? (cal as LiftCalendar) : undefined;
}

export function CalendarLanes(props: CalendarLanesProps) {
  const {
    siteId,
    view,
    dateKey,
    todayKey,
    timeZone,
    nowMs,
    data,
    robotFilter,
    fullDay,
    onNavigate,
  } = props;

  const [collapsed, setCollapsed] = useState<Set<string>>(() => loadCollapsed(siteId));
  useEffect(() => setCollapsed(loadCollapsed(siteId)), [siteId]);
  const onToggleGroup = useCallback(
    (id: string) =>
      setCollapsed((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        saveCollapsed(siteId, next);
        return next;
      }),
    [siteId],
  );

  // The filter box is local while typing; the URL follows it.
  const [filterText, setFilterText] = useState(robotFilter);
  useEffect(() => setFilterText(robotFilter), [robotFilter]);

  const board = useMemo(
    () => buildBoard(data.items, data.groups, data.robots, filterText),
    [data.items, data.groups, data.robots, filterText],
  );

  const [density, setDensity] = useState<LaneDensity>(loadLaneDensity);
  const compact = density === 'compact';
  const chooseDensity = (d: LaneDensity) => {
    setDensity(d);
    saveLaneDensity(d);
  };

  const [selectedId, setSelectedId] = useState<string | null>(null);
  /** The chip the selection came from: focus goes back to it on close. */
  const triggerRef = useRef<HTMLElement | null>(null);
  const onSelect = useCallback((id: string, trigger?: HTMLElement | null) => {
    triggerRef.current = trigger ?? null;
    setReselect(null);
    setSelectedId((cur) => (cur === id ? null : id));
  }, []);
  const selectedRef = useRef(selectedId);
  selectedRef.current = selectedId;
  // After a save from a run's panel: which runs may stand in for the
  // selected one once its id goes (`savedSchedule`), and on which day.
  const [reselect, setReselect] = useState<{ scheduleId: string; dayKey: DateKey } | null>(null);
  const closePanel = useCallback(() => {
    const cur = selectedRef.current;
    setReselect(null);
    setSelectedId(null);
    if (cur === null) return;
    const back =
      triggerRef.current?.isConnected === true
        ? triggerRef.current
        : document.querySelector<HTMLElement>(
            `[data-testid="cal-event-${CSS.escape(cur)}"], [data-agenda-id="${CSS.escape(cur)}"]`,
          );
    back?.focus();
  }, []);
  // A selection belongs to the day it was made on: paging clears it,
  // and so does a change of view. The day moving on at midnight under a
  // calendar that follows today is not paging: an open panel stays,
  // and says what midnight changed -- a carry-over that ended then
  // reads "Ended" (`SchedulePanel`).
  const pageKey = props.followsToday ? 'today' : dateKey;
  useEffect(() => setSelectedId(null), [pageKey, view]);
  // What the Agenda's last schedule action came to ("Schedule resumed.")
  // belongs to the panel it was pressed in: another schedule or run
  // selected, or the panel closed, and it goes -- before the new panel
  // is drawn -- as the phone's sheets do.
  const resetActionStatus = useRef(props.agenda?.actions.resetStatus);
  resetActionStatus.current = props.agenda?.actions.resetStatus;
  const statusOf = useRef(selectedId);
  useLayoutEffect(() => {
    if (statusOf.current === selectedId) return;
    statusOf.current = selectedId;
    resetActionStatus.current?.();
  }, [selectedId]);
  // A Remove confirmed in the panel closes it once the blocked time is
  // gone. It used to stay open on "The task you selected is not in this
  // view any more" -- the normal end of a deliberate removal, calling the
  // blocked time a task (review BB-B-3, 2026-10-31). Only on success: a
  // refusal leaves the panel, and the bar, where they were, under the
  // screen's error line. Only if the same bar is still selected.
  const deleteFromPanel = useMemo(() => {
    const remove = props.onDeleteMaintenance;
    if (!remove) return undefined;
    return (w: MaintenanceWindow) => {
      const asked = selectedRef.current;
      void Promise.resolve(remove(w)).then((removed) => {
        if (removed === true && selectedRef.current === asked) closePanel();
      });
    };
  }, [props.onDeleteMaintenance, closePanel]);
  const panelOpen = (view === 'day' || view === 'agenda') && selectedId !== null;
  useEffect(() => {
    if (!panelOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      // A dialog over the calendar (the scope question, a list) has its
      // own Esc.
      const t = e.target instanceof Element ? e.target : null;
      if (t?.closest('[role="dialog"], [aria-modal="true"], dialog')) return;
      e.preventDefault();
      closePanel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [panelOpen, closePanel]);
  const selectedItem = selectedId ? (data.items.find((it) => it.id === selectedId) ?? null) : null;
  const selectedSite = selectedId ? (data.siteBars.find((b) => b.id === selectedId) ?? null) : null;

  // A save from the panel of a run of that schedule: remember the run's
  // day -- where it sits while it is still drawn, else the day its id
  // (`occ:<schedule>:<instant>`) names.
  const saved = props.savedSchedule ?? null;
  const [savedSeen, setSavedSeen] = useState(saved?.nonce ?? null);
  if (saved && saved.nonce !== savedSeen) {
    setSavedSeen(saved.nonce);
    const prefix = `occ:${saved.scheduleId}:`;
    const runMs = selectedId?.startsWith(prefix)
      ? (selectedItem?.startMs ?? Number(selectedId.slice(prefix.length)))
      : NaN;
    setReselect(
      Number.isFinite(runMs)
        ? { scheduleId: saved.scheduleId, dayKey: dateKeyOf(runMs, timeZone) }
        : null,
    );
  }
  // ...and once the refetch has taken that id away, the same schedule's
  // run on that day -- or its carry-over's, a row of the same plan that
  // keeps the old time -- in its place. Set during render, so the panel
  // never draws "not in this view" and its focused button stays put.
  if (reselect && selectedId !== null && !selectedItem && !selectedSite) {
    const edited = { schedule_id: reselect.scheduleId };
    const stand = data.items.find(
      (it) =>
        it.occurrence !== undefined &&
        samePlan(it.occurrence, edited) &&
        dateKeyOf(it.startMs, timeZone) === reselect.dayKey,
    );
    if (stand) {
      setSelectedId(stand.id);
      setReselect(null);
    }
  }

  const [blocking, setBlocking] = useState(false);
  useEffect(() => {
    if (!props.onCreateMaintenance) setBlocking(false);
  }, [props.onCreateMaintenance]);
  useEffect(() => {
    if (props.blockPreset && props.onCreateMaintenance) setBlocking(true);
  }, [props.blockPreset, props.onCreateMaintenance]);

  const robotsByKey = useMemo(() => new Map(data.robots.map((r) => [r.key, r])), [data.robots]);
  const robotLabel = useCallback(
    (key: string | null) =>
      key ? (robotsByKey.get(key)?.label ?? key.slice(key.indexOf('/') + 1)) : null,
    [robotsByKey],
  );

  // What the lanes on screen hold -- after "Find robot", so the numbers
  // describe what the reader is looking at.
  const summary = useMemo(() => {
    const shown = [
      ...(board.topAny?.items ?? []),
      ...board.groups.flatMap((g) => g.rows.flatMap((r) => r.items)),
    ];
    return {
      tasks: shown.length,
      didntFinish: shown.filter((it) => DIDNT_FINISH.has(it.outcome)).length,
      scheduled: shown.filter((it) => it.outcome === 'scheduled' || it.outcome === 'queued').length,
    };
  }, [board]);

  const paintsPresent = useMemo<LanePaint[]>(() => {
    const present = new Set(data.items.map(paintOf));
    return LANE_PAINTS.filter((c) => present.has(c));
  }, [data.items]);

  // The Agenda pages by day, as Day does: it starts at the chosen day.
  const byDay = view !== 'week';
  const step = byDay ? 1 : 7;
  const isCurrent = byDay ? dateKey === todayKey : weekLabel(dateKey) === weekLabel(todayKey);
  const browserZone = browserTimeZone();
  const onSkip = props.onSkipOccurrence;
  const skipFromBar = useCallback((it: LaneItem) => onSkip?.(it), [onSkip]);

  const viewTabs = (['day', 'week', 'month', 'agenda'] as const).map((v) => (
    <button
      key={v}
      type="button"
      role="tab"
      aria-selected={view === v}
      className={`nx-lanes__tab${view === v ? ' is-active' : ''}`}
      onClick={() => onNavigate({ cal: v })}
      data-testid={`task-cal-view-${v}`}
    >
      {v[0]!.toUpperCase() + v.slice(1)}
    </button>
  ));

  const lanePanel = (
    <LanePanel
      item={selectedItem}
      site={selectedSite}
      stale={!selectedItem && !selectedSite}
      timeZone={timeZone}
      schedules={props.schedules}
      robotLabel={robotLabel}
      onClose={closePanel}
      onOpenTask={props.onOpenTask}
      onSkipOccurrence={props.onSkipOccurrence}
      onMoveOccurrence={props.onMoveOccurrence}
      onPauseSeries={props.onPauseSeries}
      onEditSchedule={props.onEditSchedule}
      actionsOff={props.lookOnly}
      mySub={props.mySub ?? props.agenda?.mySub ?? null}
      onDeleteMaintenance={deleteFromPanel}
      liftCalendar={props.liftCalendar}
      renderTaskActions={props.renderTaskActions}
    />
  );
  const agenda = view === 'agenda' ? props.agenda : undefined;
  const linen = useMemo(
    () => linenRobotKeys(data.robots, data.groups),
    [data.robots, data.groups],
  );
  // The schedule panel's rows: the cards' own (`AgendaView` builds the
  // same list for the cards).
  const scheduleRows = useMemo(
    () =>
      agenda
        ? buildScheduleRows({
            schedules: props.schedules,
            occurrences: agenda.occurrences,
            nowMs,
            timeZone,
            labelOf: robotLabel,
            agenda: true,
          })
        : [],
    [agenda, props.schedules, nowMs, timeZone, robotLabel],
  );
  /** The open panel's row: the cards' own, or -- for a schedule an edit
   *  has just ended, which the cards no longer list -- one built for it
   *  alone, so the panel the operator is reading does not vanish. */
  const panelScheduleRow = (id: string) => {
    const listed = scheduleRows.find((r) => scheduleSelectionId(r.schedule.schedule_id) === id);
    if (listed || !agenda) return listed ?? null;
    const s = props.schedules.find((x) => scheduleSelectionId(x.schedule_id) === id);
    return s
      ? (buildScheduleRows({
          schedules: [s],
          occurrences: agenda.occurrences,
          nowMs,
          timeZone,
          labelOf: robotLabel,
          agenda: true,
          listAll: true,
        })[0] ?? null)
      : null;
  };

  const nameW = (compact ? COMPACT_METRICS : COMFY_METRICS).nameW;
  // A view-as preview may look, never drag; the panel's field stays (its
  // write is refused before it is sent, as everywhere in a preview).
  const dragMove = props.lookOnly ? undefined : props.onMoveOccurrence;

  return (
    <section
      className={`nx-lanes${compact ? ' is-compact' : ''}`}
      style={{ ['--nx-lanes-name-w' as string]: `${nameW}px` }}
      data-testid="task-calendar"
      data-view={view}
      data-density={density}
      aria-label="Task calendar"
    >
      <div className="nx-lanes__toolbar">
        <div className="nx-lanes__tabs" role="tablist" aria-label="Calendar view">
          {viewTabs}
        </div>
        <div className="nx-lanes__nav">
          <button
            type="button"
            className="nx-lane-btn nx-lane-btn--icon"
            aria-label={byDay ? 'Previous day' : 'Previous week'}
            onClick={() => onNavigate({ date: addDays(dateKey, -step) })}
            data-testid="task-cal-prev"
          >
            ‹
          </button>
          <span className="nx-lanes__cursor" data-testid="task-cal-cursor" aria-live="polite">
            {byDay ? <DayCursor dateKey={dateKey} /> : weekLabel(dateKey)}
          </span>
          <button
            type="button"
            className="nx-lane-btn nx-lane-btn--icon"
            aria-label={byDay ? 'Next day' : 'Next week'}
            onClick={() => onNavigate({ date: addDays(dateKey, step) })}
            data-testid="task-cal-next"
          >
            ›
          </button>
          <button
            type="button"
            className="nx-lane-btn"
            disabled={isCurrent}
            onClick={() => onNavigate({ date: null })}
            data-testid="task-cal-today"
          >
            {byDay ? (
              'Today'
            ) : (
              <>
                {/* "Today" at 1280, where the toolbar has no room for more. */}
                <span className="nx-lanes__this-week">This week</span>
                <span className="nx-lanes__this-week-short">Today</span>
              </>
            )}
          </button>
        </div>
        <div className="nx-lanes__actions">
          <label className="nx-lanes__find">
            <span>{view === 'agenda' ? 'Find' : 'Find robot'}</span>
            <input
              type="search"
              value={filterText}
              placeholder="Name or floor"
              aria-label={
                view === 'agenda'
                  ? 'Find tasks by robot, floor, zone or place'
                  : 'Find robot by name or floor'
              }
              onChange={(e) => {
                setFilterText(e.target.value);
                onNavigate({ robot: e.target.value });
              }}
              data-testid="cal-find-robot"
            />
          </label>
          {view !== 'agenda' && (
            <>
              <div className="nx-lanes__seg" role="group" aria-label="Row density">
                <button
                  type="button"
                  aria-pressed={!compact}
                  aria-label="Comfortable rows"
                  title="Comfortable rows: tasks stacked, nothing merged"
                  className={!compact ? 'is-active' : ''}
                  onClick={() => chooseDensity('comfy')}
                  data-testid="cal-density-comfy"
                >
                  Comfy
                </button>
                <button
                  type="button"
                  aria-pressed={compact}
                  aria-label="Compact rows"
                  title="Compact rows: one line per robot"
                  className={compact ? 'is-active' : ''}
                  onClick={() => chooseDensity('compact')}
                  data-testid="cal-density-compact"
                >
                  Compact
                </button>
              </div>
              <div className="nx-lanes__seg" role="group" aria-label="Hours shown">
                <button
                  type="button"
                  aria-pressed={!fullDay}
                  className={!fullDay ? 'is-active' : ''}
                  onClick={() => onNavigate({ fullDay: false })}
                  data-testid="cal-hours-work"
                >
                  07–19
                </button>
                <button
                  type="button"
                  aria-pressed={fullDay}
                  className={fullDay ? 'is-active' : ''}
                  onClick={() => onNavigate({ fullDay: true })}
                  data-testid="cal-hours-full"
                >
                  24 h
                </button>
              </div>
            </>
          )}
          {props.onCreateMaintenance && (
            <button
              type="button"
              className={`nx-lane-btn${blocking ? ' is-on' : ''}`}
              aria-pressed={blocking}
              onClick={() => setBlocking((b) => !b)}
              title={
                props.liftCalendar?.create
                  ? 'Hold all tasks at this site, or set a lift rule, for a period'
                  : 'Mark a period during which this site dispatches nothing'
              }
              data-testid="task-cal-block-toggle"
            >
              Block time
            </button>
          )}
          {props.onNewTask && (
            <button
              type="button"
              className="nx-lane-btn nx-lane-btn--primary"
              onClick={props.onNewTask}
              data-testid="cal-new-task"
            >
              + New task
            </button>
          )}
        </div>
      </div>

      <div className="nx-lanes__meta">
        <span data-testid="task-cal-zone" className={browserZone === timeZone ? '' : 'is-differs'}>
          {browserZone === timeZone
            ? `Times in ${timeZoneLabel(timeZone, nowMs)}`
            : `Times are the site’s, ${timeZoneLabel(timeZone, nowMs)} — your clock is ${timeZoneLabel(browserZone, nowMs)}`}
          {dateKey === todayKey || view !== 'day'
            ? ` · now ${formatZonedClock(nowMs, timeZone)}`
            : ''}
        </span>
        {view === 'day' && (
          <span data-testid="cal-summary">
            {summary.tasks === 1 ? '1 task' : `${summary.tasks} tasks`} in view ·{' '}
            {summary.scheduled} still to run
            {summary.didntFinish ? (
              <>
                {' · '}
                <span className="nx-lanes__meta-fail">
                  {summary.didntFinish} didn’t finish
                </span>
              </>
            ) : null}
          </span>
        )}
        {data.historyLoading && (
          <span data-testid="cal-history-loading">Loading finished tasks…</span>
        )}
        {data.historyError && (
          <span className="is-warn" data-testid="cal-history-error">
            Finished tasks for this range could not be loaded — past work may be missing.
          </span>
        )}
        {data.historyPartial && (
          <span className="is-warn" data-testid="cal-history-partial">
            Only part of this range’s finished tasks loaded — choose a day to see all of it.
          </span>
        )}
        {data.fireHistoryMissing && (
          <span data-testid="cal-fire-current-only">
            Past fire events are not available from this server.
          </span>
        )}
        {props.editGroupsHref && data.canEditGroups && (
          <EditGroupsLink href={props.editGroupsHref} />
        )}
      </div>

      {blocking && props.onCreateMaintenance && (
        <BlockTimeDialog
          day={view !== 'week' ? dateKey : todayKey}
          timeZone={timeZone}
          defaultFrom={
            dateKey === todayKey
              ? `${String((Number(formatZonedClock(nowMs, timeZone).slice(0, 2)) + 1) % 24).padStart(2, '0')}:00`
              : '09:00'
          }
          onCancel={() => setBlocking(false)}
          onConfirm={(range) => {
            props.onCreateMaintenance!(range);
            setBlocking(false);
          }}
          lifts={blockTimeLifts(props.liftCalendar)}
          preset={props.blockPreset}
        />
      )}

      {view === 'agenda' && agenda ? (
        <div className="nx-lanes__body nx-lanes__body--agenda">
          <AgendaView
            dateKey={dateKey}
            todayKey={todayKey}
            days={agendaDays(dateKey, agenda.days)}
            timeZone={timeZone}
            nowMs={nowMs}
            data={data}
            query={filterText}
            schedules={props.schedules}
            occurrences={agenda.occurrences}
            horizonDays={agenda.horizonDays}
            issues={agenda.issues}
            selectedId={selectedId}
            onSelect={onSelect}
            onShowMore={
              agenda.days < AGENDA_MAX_DAYS
                ? () =>
                    onNavigate({ days: Math.min(AGENDA_MAX_DAYS, agenda.days + AGENDA_STEP_DAYS) })
                : undefined
            }
            onOpenDay={(day) => onNavigate({ cal: 'day', date: day })}
            onDeleteMaintenance={props.onDeleteMaintenance}
            robotLabel={robotLabel}
            mySub={props.mySub ?? agenda.mySub}
            panel={
              panelOpen ? (
                selectedId!.startsWith('schedule:') ? (
                  <SchedulePanel
                    key={selectedId}
                    row={panelScheduleRow(selectedId!)}
                    schedules={props.schedules}
                    occurrences={agenda.occurrences}
                    nowMs={nowMs}
                    timeZone={timeZone}
                    horizonDays={agenda.horizonDays}
                    actions={agenda.actions}
                    mySub={agenda.mySub}
                    linen={linen}
                    onEdit={
                      props.onEditSchedule
                        ? (trigger) =>
                            props.onEditSchedule!({
                              scheduleId: selectedId!.slice('schedule:'.length),
                              trigger,
                            })
                        : undefined
                    }
                    onClose={closePanel}
                    onSelectRun={(o) => {
                      const id = `occ:${o.schedule_id}:${o.original_start_ms}`;
                      if (data.items.some((it) => it.id === id)) onSelect(id);
                    }}
                  />
                ) : (
                  lanePanel
                )
              ) : null
            }
          />
        </div>
      ) : (
        <div className={`nx-lanes__body${view === 'week' ? ' nx-lanes__body--week' : ''}`}>
          <div className="nx-lanes__main">
            {view === 'day' ? (
              <DayLanes
                dateKey={dateKey}
                timeZone={timeZone}
                nowMs={nowMs}
                fullDay={fullDay}
                board={board}
                siteBars={data.siteBars}
                collapsed={collapsed}
                onToggleGroup={onToggleGroup}
                selectedId={selectedId}
                onSelect={onSelect}
                onCreateAtSlot={props.onCreateAtSlot}
                onSkipOccurrence={onSkip ? skipFromBar : undefined}
                compact={compact}
                onMoveOccurrence={dragMove}
              />
            ) : (
              <WeekLanes
                dateKey={dateKey}
                todayKey={todayKey}
                timeZone={timeZone}
                fullDay={fullDay}
                board={board}
                siteBars={data.siteBars}
                collapsed={collapsed}
                onToggleGroup={onToggleGroup}
                onOpenDay={(day, robot) =>
                  onNavigate({ cal: 'day', date: day, ...(robot !== null ? { robot } : {}) })
                }
              />
            )}
            <div className="nx-lanes__legend" data-testid="cal-legend">
              {paintsPresent.map((l) => (
                <span
                  key={l}
                  className="nx-lanes__legend-item"
                  style={laneVars(l)}
                  data-testid={`cal-legend-${l}`}
                >
                  <span className="nx-lanes__legend-code" aria-hidden="true">
                    {LANE_CODE[l]}
                  </span>
                  {paintLabel(l)}
                </span>
              ))}
              {view === 'day' && data.items.some((it) => it.high) && (
                <span className="nx-lanes__legend-item" data-testid="cal-legend-high">
                  <span
                    className="nx-lanes__legend-code nx-lanes__legend-code--high"
                    aria-hidden="true"
                  />
                  High priority
                </span>
              )}
              {compact && view === 'day' && (
                <span className="nx-lanes__legend-item" data-testid="cal-legend-count">
                  <span className="nx-lanes__legend-code nx-lanes__legend-code--count">3</span>=
                  tasks too close to draw apart
                </span>
              )}
              <span className="nx-lanes__legend-key">
                Solid = done · outline = queued · dashed = scheduled · red = didn’t finish · automatic
                charging and returns to dock are not shown
                {view === 'week' ? ' · click a cell for that robot’s day' : ''}
              </span>
            </div>
          </div>
          {panelOpen && lanePanel}
        </div>
      )}
    </section>
  );
}

/** "Thursday 22/Oct/2026"; at 1280 the weekday shortens to "Thu" (every
 *  weekday's first three letters are its abbreviation) so the toolbar
 *  stays on one line. */
function DayCursor({ dateKey }: { dateKey: DateKey }) {
  const wd = WEEKDAY_LONG[weekdayOf(dateKey)]!;
  return (
    <>
      {wd.slice(0, 3)}
      <span className="nx-lanes__wd-rest">{wd.slice(3)}</span> {formatKey(dateKey)}
    </>
  );
}

function EditGroupsLink({ href }: { href: string }) {
  const inRouter = useInRouterContext();
  return inRouter ? (
    <Link to={href} className="nx-lanes__edit-groups" data-testid="cal-edit-groups">
      Edit groups
    </Link>
  ) : (
    <a href={href} className="nx-lanes__edit-groups" data-testid="cal-edit-groups">
      Edit groups
    </a>
  );
}
